/* Leaft Blade V2 — buy box behaviour.
   INP budget 200 ms: selection and toggling only swap text and data attributes;
   rows, prices and both modes are all rendered server-side. The only network
   call on the page is add-to-cart (handled by Horizon's product-form-component). */

(() => {
  if (customElements.get('lb2-buy-box')) return;

  class LB2BuyBox extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.lb2-bb');
      this.rows = [...this.querySelectorAll('[data-lb2-tier]')];
      this.modeInputs = [...this.querySelectorAll('[data-lb2-mode-input]')];
      this.seriesInputs = [...this.querySelectorAll('[data-lb2-series-input]')];
      this.freqRow = this.querySelector('[data-lb2-freq]');
      this.variantInput = this.querySelector('[data-lb2-form-variant]');
      this.freqInput = this.querySelector('[data-lb2-form-freq]');
      this.planInput = this.querySelector('[data-lb2-form-plan]');
      this.ctaLabel = this.querySelector('[data-lb2-cta-label]');
      this.sticky = this.section ? this.section.querySelector('[data-lb2-sticky]') : null;
      this.stickyLine = this.section ? this.section.querySelector('[data-lb2-sticky-line]') : null;
      this.stickySub = this.section ? this.section.querySelector('[data-lb2-sticky-sub]') : null;
      this.cta = this.querySelector('.lb2-bb__cta');
      // Elements whose text swaps with the series (badge, toggle subs, footnote numbers).
      this.seriesTexts = [...this.querySelectorAll('[data-lb2-badge], [data-lb2-sub-save], [data-lb2-onetime-sub], [data-lb2-flat-per]')];

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-lb2-tier-input]')) this.update();
        if (e.target.matches('[data-lb2-mode-input]')) this.setMode(e.target.value);
        if (e.target.matches('[data-lb2-series-input]')) this.setSeries(e.target.value);
        if (e.target.matches('[data-lb2-freq]')) {
          // A hand-picked frequency is a decision; stop auto-following the box
          // size from here on.
          this.freqTouched = true;
          this.update();
        }
      });

      // Deep links: ?variant_series=Pro selects the second series on load
      // (the "Drug-tested athlete?" links point here with that param), and
      // ?box=N selects that ladder row (the home freezer chips point here).
      const params = new URLSearchParams(window.location.search);
      if (this.seriesInputs.length && this.dataset.seriesB) {
        const want = params.get('variant_series');
        if (want && want.toLowerCase() === this.dataset.seriesB.toLowerCase()) {
          const input = this.seriesInputs.find((i) => i.value === 'b');
          if (input) input.checked = true;
          this.dataset.series = 'b';
        }
      }
      const box = params.get('box');
      if (box) {
        const row = this.rows.find((r) => r.dataset.blades === box);
        if (row) row.querySelector('input').checked = true;
      }

      this.initPackShot();
      this.initGallery();
      this.initSticky();
      this.setSeries(this.series);
    }

    get mode() {
      return this.dataset.mode || 'sub';
    }

    get series() {
      return this.dataset.series === 'b' ? 'b' : 'a';
    }

    get selectedRow() {
      return this.rows.find((r) => r.querySelector('input').checked) || this.rows[0];
    }

    // All prices for a row in the active series, pre-formatted server-side.
    rowData(row) {
      const d = row.dataset;
      if (this.series === 'b' && d.bSubVariant) {
        return {
          subVariant: d.bSubVariant, oneVariant: d.bOneVariant,
          subPer: d.bSubPer, onePer: d.bOnePer,
          subBox: d.bSubBox, oneBox: d.bOneBox,
          save: parseInt(d.bSave, 10) || 0,
        };
      }
      return {
        subVariant: d.subVariant, oneVariant: d.oneVariant,
        subPer: d.subPer, onePer: d.onePer,
        subBox: d.subBox, oneBox: d.oneBox,
        save: parseInt(d.save, 10) || 0,
      };
    }

    get maxSave() {
      return this.rows.reduce((m, row) => Math.max(m, this.rowData(row).save), 0);
    }

    get maxSaveBlades() {
      let best = 0, blades = '';
      this.rows.forEach((row) => {
        const save = this.rowData(row).save;
        if (save > best) { best = save; blades = row.dataset.blades; }
      });
      return blades;
    }

    setSeries(series) {
      this.dataset.series = series;
      // Badge, toggle subs and footnote numbers carry both series' text.
      this.seriesTexts.forEach((el) => {
        const text = series === 'b' ? el.dataset.b : el.dataset.a;
        if (text) el.textContent = text;
      });
      this.setMode(this.mode);
    }

    setMode(mode) {
      this.dataset.mode = mode;
      // Per-row per-Blade price text swaps between the pre-formatted values.
      this.rows.forEach((row) => {
        const d = this.rowData(row);
        const per = row.querySelector('[data-lb2-row-per]');
        const was = row.querySelector('[data-lb2-row-was]');
        const saveTag = row.querySelector('[data-lb2-row-save]');
        const totalText = row.querySelector('[data-lb2-row-total-text]');
        if (mode === 'onetime') {
          per.textContent = d.onePer;
          totalText.textContent = `${d.oneBox} a box, bought once`;
        } else {
          per.textContent = d.subPer;
          totalText.textContent = `${d.subBox} a box, delivered ${this.freqLabel()}`;
        }
        // Pro's 7-box subscribes at the one-time price: no strike-through, no 0% tag.
        if (was) {
          was.textContent = d.onePer;
          was.style.display = d.save > 0 ? '' : 'none';
        }
        if (saveTag) {
          saveTag.textContent = `Save ${d.save}%`;
          saveTag.style.visibility = d.save > 0 ? '' : 'hidden';
        }
      });
      this.update();
    }

    // The selling plan for the selected row at the selected cadence. Rows carry
    // a {weeks: planId} map rendered from the sub product's Recharge plans;
    // an empty result means this product has no plans and the form falls back
    // to the Delivery line-item property.
    selectedPlan(row) {
      if (!this.freqRow || !row.dataset.plans) return '';
      const weeks = this.freqRow.selectedOptions[0]?.dataset.weeks || '1';
      try {
        return JSON.parse(row.dataset.plans)[weeks] || '';
      } catch {
        return '';
      }
    }

    freqLabel() {
      if (!this.freqRow) return 'weekly';
      const label = this.freqRow.value || 'Every week';
      const l = label.toLowerCase();
      if (l === 'every week') return 'weekly';
      if (l === 'every 2 weeks' || l === 'every fortnight') return 'fortnightly';
      if (l === 'every 4 weeks' || l === 'every month') return 'monthly';
      return l;
    }

    // How the selected box size lands at a given interval, stated honestly:
    // 28 Blades / 4 weeks is exactly one a day; 7 / 2 weeks is 3 to 4 a week.
    cadence(blades, weeks) {
      const perWeek = blades / weeks;
      const perDay = perWeek / 7;
      if (Number.isInteger(perDay)) return perDay === 1 ? 'matches one a day' : `matches ${perDay} a day`;
      if (perDay > 1) return `${Math.floor(perDay)} to ${Math.ceil(perDay)} a day`;
      if (Number.isInteger(perWeek)) return `${perWeek} a week`;
      return `${Math.floor(perWeek)} to ${Math.ceil(perWeek)} a week`;
    }

    // Until the shopper picks a frequency themselves, the cadence follows the
    // box size at one Blade a day: 7 ships weekly, 14 every 2 weeks, 21 every
    // 3, 28 every 4. A box size without a matching interval leaves the
    // selection alone.
    syncFreqToTier() {
      if (!this.freqRow || this.freqTouched) return;
      const blades = parseInt(this.selectedRow?.dataset.blades, 10);
      if (!blades || blades % 7 !== 0) return;
      const weeks = String(blades / 7);
      const match = [...this.freqRow.options].find((opt) => opt.dataset.weeks === weeks);
      if (match && !match.selected) this.freqRow.value = match.value;
    }

    updateFreqHints() {
      if (!this.freqRow) return;
      const blades = parseInt(this.selectedRow?.dataset.blades, 10);
      if (!blades) return;
      [...this.freqRow.options].forEach((opt) => {
        const note = opt.dataset.hint || this.cadence(blades, parseInt(opt.dataset.weeks, 10) || 1);
        opt.textContent = `${opt.value} · ${note}`;
      });
    }

    update() {
      const row = this.selectedRow;
      if (!row) return;
      this.syncFreqToTier();
      this.updateFreqHints();
      const d = this.rowData(row);
      const sub = this.mode === 'sub';
      const blades = row.dataset.blades;
      const box = sub ? d.subBox : d.oneBox;
      const per = sub ? d.subPer : d.onePer;
      const save = d.save;

      // Refresh the selected row's total line for the current frequency.
      const totalText = row.querySelector('[data-lb2-row-total-text]');
      totalText.textContent = sub
        ? `${d.subBox} a box, delivered ${this.freqLabel()}`
        : `${d.oneBox} a box, bought once`;

      // The pack render hangs off the main product's variant, so it follows the
      // one-time id in both modes: the subscription products carry no media.
      this.setPackShot(d.oneVariant);

      // Form state.
      this.variantInput.value = sub ? d.subVariant : d.oneVariant;
      const planId = sub ? String(this.selectedPlan(row)) : '';
      if (this.planInput) {
        this.planInput.disabled = planId === '';
        this.planInput.value = planId;
      }
      if (this.freqInput) {
        // With a real selling plan attached the plan itself carries the
        // cadence, so the Delivery property only submits as the fallback.
        this.freqInput.disabled = !sub || planId !== '';
        if (sub && this.freqRow) this.freqInput.value = this.freqRow.value;
      }

      // CTA label. Leads with the phrase shoppers scan a product page for;
      // the box total keeps the price honest next to the click.
      this.ctaLabel.textContent = `Add to cart · ${box}`;

      // Sticky bar.
      if (this.stickyLine) {
        this.stickyLine.textContent = `${blades} Blades · ${per} a Blade`;
        if (!sub) {
          this.stickySub.textContent = this.maxSave > 0
            ? `bought once · subscribe and save up to ${this.maxSave}%`
            : 'bought once';
        } else if (save >= this.maxSave) {
          this.stickySub.textContent = `save ${save}% · free shipping on this box`;
        } else if (save === 0) {
          this.stickySub.textContent = `up to ${this.maxSave}% at ${this.maxSaveBlades} Blades`;
        } else {
          this.stickySub.textContent = `save ${save}% now · ${this.maxSave}% at ${this.maxSaveBlades} Blades`;
        }
      }
    }

    // Gallery carousel. The track's scroll position is the source of truth:
    // arrows and thumbs scroll it, and everything else reacts to where it
    // lands. That way a swipe, a click and a keypress all end up in the same
    // state without anyone having to keep a separate index in sync.
    // The first slide is the selected variant's pack render. The ladder and the
    // series switch pick variants without a native picker, so the swap is done
    // here from a map the section prints alongside the carousel.
    initPackShot() {
      const root = this.section;
      if (!root) return;
      const data = root.querySelector('[data-lb2-pack-map]');
      this.packSlide = root.querySelector('[data-lb2-pack] img');
      this.packThumb = root.querySelector('[data-lb2-pack-thumb] img');
      if (!data || !this.packSlide) return;
      try {
        this.packMap = JSON.parse(data.textContent);
      } catch (e) {
        this.packMap = null;
      }
    }

    setPackShot(variantId) {
      if (!this.packMap || !this.packSlide || !variantId) return;
      const key = String(variantId);
      // Keyed on the variant, not the src: two variants can legitimately share
      // an image while differing in alt text.
      if (this.packVariantId === key) return;
      const shot = this.packMap[key];
      if (!shot) return;
      this.packVariantId = key;
      this.packSlide.src = shot.src;
      if (shot.srcset) this.packSlide.srcset = shot.srcset;
      if (shot.alt) this.packSlide.alt = shot.alt;
      if (this.packThumb && shot.thumb) this.packThumb.src = shot.thumb;
    }

    initGallery() {
      const root = this.section;
      if (!root) return;
      const track = root.querySelector('[data-lb2-track]');
      if (!track) return;

      const slides = [...track.querySelectorAll('[data-lb2-slide]')];
      const thumbs = [...root.querySelectorAll('[data-lb2-thumb]')];
      const prev = root.querySelector('[data-lb2-prev]');
      const next = root.querySelector('[data-lb2-next]');
      if (slides.length < 2) return;

      let current = 0;

      const goTo = (i) => {
        const slide = slides[Math.max(0, Math.min(i, slides.length - 1))];
        if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft });
      };

      const sync = (i) => {
        current = i;
        thumbs.forEach((btn) => {
          const on = Number(btn.dataset.lb2Thumb) === i;
          btn.classList.toggle('is-active', on);
          if (on) btn.setAttribute('aria-current', 'true');
          else btn.removeAttribute('aria-current');
        });
        // At either end the matching arrow has nothing to do, so it goes away
        // rather than sitting there dead.
        // Marked disabled rather than hidden: removing the element the shopper
        // has just focused drops focus to the top of the document.
        if (prev) prev.setAttribute('aria-disabled', String(i === 0));
        if (next) next.setAttribute('aria-disabled', String(i === slides.length - 1));
        // Media that scrolls away stops playing, and nothing off-screen holds
        // focus or gets read out.
        slides.forEach((s, n) => {
          if (n !== i) s.querySelectorAll('video, audio').forEach((m) => !m.paused && m.pause());
          s.inert = n !== i;
        });
        // With a dozen media the thumb row scrolls, so keep the current one in
        // view rather than leaving the shopper looking at an inactive strip.
        const active = thumbs[i];
        if (active && active.parentElement && active.parentElement.scrollWidth > active.parentElement.clientWidth) {
          const row = active.parentElement;
          const left = active.offsetLeft - (row.clientWidth - active.clientWidth) / 2;
          row.scrollTo({ left: Math.max(0, left) });
        }
      };

      thumbs.forEach((btn) => {
        btn.addEventListener('click', () => goTo(Number(btn.dataset.lb2Thumb)));
      });
      const step = (delta) => (e) => {
        if (e.currentTarget.getAttribute('aria-disabled') === 'true') return;
        goTo(current + delta);
      };
      if (prev) prev.addEventListener('click', step(-1));
      if (next) next.addEventListener('click', step(1));

      track.addEventListener('keydown', (e) => {
        // Only when the track itself has focus. Media curated on the product can
        // include a <video controls>, whose own arrow keys seek; keydown bubbles,
        // so without this guard the carousel would swallow them.
        if (e.target !== track) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      });

      // Whichever slide is most visible is the current one. An observer keeps
      // this correct through a swipe, a momentum scroll or a resize, none of
      // which fire a click.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                sync(Number(entry.target.dataset.lb2Slide));
              }
            });
          },
          { root: track, threshold: [0.6] }
        );
        slides.forEach((s) => io.observe(s));
      }

      sync(0);
    }

    initSticky() {
      if (!this.sticky || !this.cta) return;
      // Pinned once the in-page CTA leaves the viewport, so two add-to-cart
      // buttons are never on screen at the same time.
      const io = new IntersectionObserver(
        ([entry]) => {
          this.sticky.hidden = entry.isIntersecting;
        },
        { rootMargin: '0px 0px -10px 0px' }
      );
      io.observe(this.cta);
    }

  }

  customElements.define('lb2-buy-box', LB2BuyBox);
})();
